import { useEffect, useRef, useState } from 'react';
import { useLabels } from 'src/StoreDataProvider';
import { RangeSliderProps } from './rangeSlider.types';

const RangeSlider: (props: RangeSliderProps) => JSX.Element = ({
  label,
  min,
  max,
  selectedMin,
  selectedMax,
  backgroundColor,
  backgroundSelectedColor,
  onChange,
}) => {
  const [leftInputFocused, setLeftInputFocused] = useState(false);
  const [rightInputFocused, setRightInputFocused] = useState(false);
  const [innerSelectedMin, setInnerSelectedMin] = useState(selectedMin);
  const [innerSelectedMax, setInnerSelectedMax] = useState(selectedMax);
  const [tempSelectedMin, setTempSelectedMin] = useState(min.toString());
  const [tempSelectedMax, setTempSelectedMax] = useState(max.toString());
  const [leftInputValid, setLeftInputValid] = useState(true);
  const [rightInputValid, setRightInputValid] = useState(true);
  const [rangeSliderDoubleRightZIndex, setRangeSliderDoubleRightZIndex] =
    useState(0);
  const [
    rangeSliderDoubleRightBackground,
    setRangeSliderDoubleRightBackground,
  ] = useState(backgroundColor);
  const labels = useLabels();

  useEffect(() => {
    fillSlider();
    setRightToggleAccessible();
  }, []);

  useEffect(() => {
    setInnerSelectedMin(selectedMin);
  }, [selectedMin]);

  useEffect(() => {
    setInnerSelectedMax(selectedMax);
  }, [selectedMax]);

  useEffect(() => {
    fillSlider();
    setTempSelectedMin(innerSelectedMin.toString());
    setLeftInputValid(true);
  }, [innerSelectedMin]);

  useEffect(() => {
    setRightToggleAccessible();
    fillSlider();
    setTempSelectedMax(innerSelectedMax.toString());
    setRightInputValid(true);
  }, [innerSelectedMax]);

  const onRangeChangeLeft = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const selectedNumber = event.target.value as unknown as number;
    if (selectedNumber <= innerSelectedMax) {
      setInnerSelectedMin(selectedNumber);
    }
  };

  const onRangeChangeRight = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const selectedNumber = event.target.value as unknown as number;
    if (selectedNumber >= innerSelectedMin) {
      setInnerSelectedMax(selectedNumber);
    }
  };

  const onInputChangeLeft = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value;
    setTempSelectedMin(value);
    let tempLeftInputValid = false;
    const numValue = parseInt(value);
    if (numValue) {
      tempLeftInputValid =
        numValue <= innerSelectedMax && numValue <= max && numValue >= min;
      if (tempLeftInputValid) {
        setInnerSelectedMin(numValue);
        emitChange();
      }
    }
    setLeftInputValid(tempLeftInputValid);
  };

  const onInputChangeRight = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value;
    setTempSelectedMax(value);
    let tempRightInputValid = false;
    const numValue = parseInt(value);
    if (numValue) {
      tempRightInputValid =
        numValue >= innerSelectedMin && numValue <= max && numValue >= min;
      if (tempRightInputValid) {
        setInnerSelectedMax(numValue);
      }
    }
    setRightInputValid(tempRightInputValid);
  };

  const setRightToggleAccessible = (): void => {
    setRangeSliderDoubleRightZIndex(
      innerSelectedMax.toString() === min.toString() ? 2 : 0
    );
  };

  const fillSlider = (): void => {
    const rangeDistance = max - min;
    const fromPosition = innerSelectedMin - min;
    const toPosition = innerSelectedMax - min;
    setRangeSliderDoubleRightBackground(`linear-gradient(
      to right,
      ${backgroundColor} 0%,
      ${backgroundColor} ${(fromPosition / rangeDistance) * 100}%,
      ${backgroundSelectedColor} ${(fromPosition / rangeDistance) * 100}%,
      ${backgroundSelectedColor} ${(toPosition / rangeDistance) * 100}%,
      ${backgroundColor} ${(toPosition / rangeDistance) * 100}%,
      ${backgroundColor} 100%)`);
  };

  const emitChange = (): void => {
    onChange(Number(innerSelectedMin), Number(innerSelectedMax));
  };

  // Added onchange handlers, because Reacts onChange fires
  // even when you are still sliding the range slider.
  // So there would be multiple requests at the same time
  // if you slide very fast, and they wouldnt be in order.
  // This onchange says that we only want to emit changes
  // when the Button is not pressed anymore.
  const rangeSliderLeftRef = useRef<HTMLInputElement>(null);
  const rangeSliderRightRef = useRef<HTMLInputElement>(null);
  const rangeInputLeftRef = useRef<HTMLInputElement>(null);
  const rangeInputRightRef = useRef<HTMLInputElement>(null);
  [
    rangeSliderLeftRef,
    rangeSliderRightRef,
    rangeInputLeftRef,
    rangeInputRightRef,
  ].forEach(value => {
    if (value.current) {
      value.current.onchange = function () {
        emitChange();
      };
    }
  });

  return (
    <fieldset className="range-slider-fieldset">
      <legend>{label}</legend>
      <div
        id="range-slider-double"
        className="range-slider"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div
          className={leftInputFocused ? 'range-slider-double__focus' : ''}
          tabIndex={-1}
        >
          <input
            tabIndex={-1}
            id="range-slider-double__left"
            type="range"
            min={min}
            max={max}
            value={innerSelectedMin}
            ref={rangeSliderLeftRef}
            onChange={onRangeChangeLeft}
          />
        </div>
        <div
          className={rightInputFocused ? 'range-slider-double__focus' : ''}
          tabIndex={-1}
        >
          <input
            tabIndex={-1}
            id="range-slider-double__right"
            type="range"
            min={min}
            max={max}
            value={innerSelectedMax}
            style={{
              zIndex: rangeSliderDoubleRightZIndex,
              background: rangeSliderDoubleRightBackground,
            }}
            ref={rangeSliderRightRef}
            onChange={onRangeChangeRight}
          />
        </div>
      </div>
      <div className="range-input">
        <div>
          <label htmlFor="minInput">Min</label>
          <input
            id="minInput"
            className={`range-input-field ${!leftInputValid ? 'error' : ''}`}
            type="number"
            aria-invalid={leftInputValid}
            aria-describedby="range-error-message"
            value={tempSelectedMin}
            onChange={onInputChangeLeft}
            ref={rangeInputLeftRef}
            onFocus={() => setLeftInputFocused(true)}
            onBlur={() => setLeftInputFocused(false)}
          />
        </div>
        <div>
          <label htmlFor="maxInput">Max</label>
          <input
            id="maxInput"
            className={`range-input-field ${!rightInputValid ? 'error' : ''}`}
            type="number"
            aria-invalid={!rightInputValid}
            aria-describedby="range-error-message"
            value={tempSelectedMax}
            onChange={onInputChangeRight}
            ref={rangeInputRightRef}
            onFocus={() => setRightInputFocused(true)}
            onBlur={() => setRightInputFocused(false)}
          />
        </div>
      </div>
      <div className="range-error-message__container">
        {(!leftInputValid || !rightInputValid) && (
          <div id="range-error-message" className="range-error-message">
            <em className="icon-achtung" aria-hidden="true" />
            <div role="alert">{labels.ratingNotValid}.</div>
          </div>
        )}
      </div>
    </fieldset>
  );
};

export default RangeSlider;
